import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useLayoutEffect } from 'react';
import { 
  Divider, Flex, Dropdown, Popover, Button, Space, Modal, Input, App, Typography, Table,
} from 'antd';
import { 
  VerticalRightOutlined, VerticalLeftOutlined, 
  DownOutlined, FileAddOutlined, LoadingOutlined,
} from '@ant-design/icons';
import _ from 'lodash';

import md5 from 'blueimp-md5';

// 引入 Ant Design X 组件
import { 
  Bubble, Conversations, Sender, ThoughtChain, XProvider,
} from '@ant-design/x';
import type { BubbleProps, ConversationsProps } from '@ant-design/x';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { WaenMarked } from 'react-waenui-mdeditor';
import isMobile from 'ismobilejs';
import './ai.css';


// ================= 类型定义 =================
interface ChatSession {
  key: string;
  label: string;
  createDate?: Date;
}

interface Message {
  key: string;
  placement?: 'start' | 'end';
  content?: string;
  // avatar?: string;
  loading?: boolean;
  loadingRender?: any;
  messageRender?: any;
}

interface AppProps {}

const { useApp } = App;

const renderMarkdown: BubbleProps['messageRender'] = (content) => {
  return (
    <Typography>
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: used in demo */}
      <div dangerouslySetInnerHTML={{ __html: WaenMarked(content) }} />
    </Typography>
  );
};

// ================= 工具函数 =================
const loadChatSessions = () => {
  const userId = localStorage.getItem('userId');
  const chatSessions = localStorage.getItem(`chatSessions${userId}`);
  if (chatSessions) {
    try {
      const parsedArray = JSON.parse(chatSessions);
      return parsedArray;
    } catch (error) {
      console.error('解析错误：', error);
      return [];
    }
  }
  return [];
};

const pushChatSessions = (session: ChatSession): void => {
  const chatSessions = loadChatSessions();
  if (chatSessions.length > 0 && chatSessions[chatSessions.length - 1].key === session.key) {
    return;
  }
  chatSessions.push(session);
  const userId = localStorage.getItem('userId');
  localStorage.setItem(`chatSessions${userId}`, JSON.stringify(chatSessions));
};

const loadMessages = (sessionId: string | null): Message[] => {
  if (!sessionId) return [];
  const messages = localStorage.getItem(`bublelist${sessionId}`);
  const msgs = messages ? JSON.parse(messages) : [];
  return msgs;
};

// ================= 组件定义 =================

/**
 * 左侧边栏组件
 */
const Sidebar: React.FC<{
  showLeftBar: boolean;
  setShowLeftBar: (show: boolean) => void;
  chatSessions: any;
  addNewSession: () => void;
  sessionId: string | null;
  changeSession: (id: string) => void;
  menuConfig: any;
  userInfo: any;
  toLogin: any;
  toLogout: any;
}> = ({ showLeftBar, setShowLeftBar, chatSessions, addNewSession, sessionId, changeSession, menuConfig, userInfo, toLogin, toLogout }) => {
  if (!showLeftBar) return null;

  return (
    <>
      <Flex vertical style={{ width: 200, height: '100%' }}>
        <Flex justify="space-between" align="center" style={{ padding: '0 12px' }}>
          <span style={{ fontSize: 18 }}>华恩智教</span>
          <Popover content="收起侧栏">
            <Button 
              onClick={() => setShowLeftBar(false)}
              icon={<VerticalRightOutlined />}
            />
          </Popover>
        </Flex>
        
        <div style={{ margin: '8px 0', padding: '0 12px' }}>
          <Button 
            type="default" 
            block 
            icon={<FileAddOutlined />}
            onClick={addNewSession}
            disabled={!sessionId}
          >
            新建对话
          </Button>
        </div>
        
        <Conversations
          style={{ width: 200, flex: 1, overflowY: 'auto' }}
          activeKey={sessionId || ''}
          items={chatSessions.map((session: ChatSession) => ({
            key: session.key,
            label: session.label,
          }))}
          onActiveChange={changeSession}
          menu={menuConfig}
        />
        <div>
          {
            userInfo.userId ? <Dropdown menu={{items:[{
              label: '首页',
              key: '1',
            },
            {
              label: '登出',
              key: '0',
            }],
            onClick: (menuInfo) => {
              menuInfo.domEvent.stopPropagation();
              if (menuInfo.key === '1') {
                window.location.href = '/'
              } else if (menuInfo.key === '0') {
                toLogout();
              }
            }
          }}
            placement="top"
            >
              <a type="text" onClick={(e) => e.preventDefault()}>
                <Space style={{ width: '200px', height: '40px' }}>
                  <div style={{ width: 156, textAlign: 'right' }}>{userInfo.userName || userInfo.userId}</div>
                  <div style={{ width: '20px', height: '20px' }}><DownOutlined /></div>
                </Space>
              </a>
          </Dropdown> :
          <Button type="text" block onClick={toLogin}>
            未登录
          </Button>
          }
        </div>
      </Flex>
      <Divider type="vertical" style={{ height: '100%' }} />
    </>
  );
};

/**
 * 聊天区域头部组件
 */
const ChatHeader: React.FC<{
  showLeftBar: boolean;
  setShowLeftBar: (show: boolean) => void;
  addNewSession: () => void;
  chatSessions: any;
  sessionId: string | null;
  menuProps: any;
}> = ({ showLeftBar, setShowLeftBar, addNewSession, chatSessions, sessionId, menuProps }) => {
  const getSessionName = (): string => {
    const session = chatSessions.find((s: ChatSession) => s.key === sessionId);
    return session?.label || '新对话';
  };

  return (
    <Flex align="center" style={{ padding: '0 10px' }}>
      {!showLeftBar && (
        <div style={{ marginRight: 20 }}>
          <Popover content="展开侧栏">
            <Button 
              onClick={() => setShowLeftBar(true)}
              icon={<VerticalLeftOutlined />}
              style={{ marginRight: 5 }}
            />
          </Popover>
          <Popover content="新建对话">
            <Button 
              onClick={addNewSession}
              icon={<FileAddOutlined />}
              disabled={!sessionId}
            />
          </Popover>
        </div>
      )}
      
      {sessionId ? (
        <Dropdown menu={menuProps} trigger={['click']}>
          <a onClick={(e) => e.preventDefault()}>
            <Space>
              {getSessionName()}
              <DownOutlined />
            </Space>
          </a>
        </Dropdown>
      ) : (
        <Space>{getSessionName()}</Space>
      )}
    </Flex>
  );
};

export interface ScrollDotHandle {
  scrollTo: () => void;
}

/**
 * 消息列表组件
 */
const MessageList = forwardRef<ScrollDotHandle, {
  messages: Message[];
  usermsg: string;
  aimsg: string;
  messageSending: string;
  shouldScroll: boolean;
  completeTyping: any;
  reAsk: any;
}>(({ messages, usermsg, aimsg, messageSending, shouldScroll, completeTyping, reAsk }, ref) => {
  // 创建子组件内部ref
  const scrolldotRef = useRef<HTMLDivElement>(null);
  const [count, setCount] = useState<any>(0);

  useEffect(() => {
    if (messageSending === 'sending') {
      setTimeout(() => {
        setCount(count + 1);
        scrolldotRef.current?.scrollIntoView({ behavior: "smooth", block: "end"  });
      }, 500);
    } else {
      setCount(0);
    }
  }, [count, messageSending])
  useLayoutEffect(() => {
      scrolldotRef.current?.scrollIntoView({ behavior: "smooth", block: "end"  });
  }, [shouldScroll, messages, aimsg, messageSending]);

  useImperativeHandle(ref, () => ({
    scrollTo: () => {
      scrolldotRef.current?.scrollIntoView({ behavior: 'smooth', block: "end" });
    }
  }));

  if (messages.length === 0 && messageSending === 'stop') {
    return (
      <div style={{ flex: 1, overflowY: 'auto', textAlign: 'center', padding: 20 }} className="message-list">
        欢迎您！我是您的专业顾问，可以为您提供以下支持：
        <br />信奥赛事政策咨询、知识点解析、解题指导、提分技巧。
      </div>
    );
  }
  return (
    <div style={{ flex: 1, overflowY: 'auto' }} className="message-list">
      {
        messages.map(msg => {
          return msg.placement === 'end' ? <Bubble key={msg.key} style={{ margin: 10 }} placement={msg.placement} content={msg.content || ''} /> : <Bubble key={msg.key} style={{ width: '90%' }} placement={msg.placement} messageRender={renderMarkdown} content={msg.content || ''} />
      })
      }
      {
        messageSending !== 'stop' ? 
        <div style={{ minHeight: '100%' }}>
          <Bubble key="user" style={{ margin: 10 }} placement="end" content={usermsg || ''} />
          <Bubble
            key="ai"
            messageRender={renderMarkdown} content={aimsg || '正在思考中...'}
            typing={messageSending !== 'interrupt'}
            style={{ width: '90%' }}
            placement="start"
            onTypingComplete={completeTyping}
            loading={messageSending === 'thinking'}
          />
          <div style={{ textAlign: 'center', display: messageSending === 'interrupt' ? 'block' : 'none' }}>
            <span>你停止生成了本次回答</span>
            <span style={{ color: 'blue', cursor: 'pointer', marginLeft: 10 }} onClick={reAsk}>重新编辑问题</span>
          </div>
        </div> 
        : null
      }
      <div ref={scrolldotRef} id="scrolldot" />
    </div>
  );
});

/**
 * 主应用组件
 */
const AiApp: React.FC<AppProps> = () => {
  // ================= 状态管理 =================
  const { sessionId: paramSessionId } = useParams();
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(paramSessionId || '');
  const [messages, setMessages] = useState<Message[]>([]);
  const [showLeftBar, setShowLeftBar] = useState<boolean>(true);
  const [messageSending, setMessageSending] = useState<string>('stop');
  const [inputValue, setInputValue] = useState<string>('');
  const [inputSessionName, setInputSessionName] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // const [lines, setLines] = useState<any>([]);
  const [usermsg, setUsermsg] = useState<string>('');
  const [aimsg, setAimsg] = useState<string>('');
  const [shouldScroll, setShouldScroll] = useState(false);
  const [rtmsg, setRtmsg] = useState<any>(null);
  const [duration, setDuration] = useState<number>(-1);
  const [userInfo, setUserInfo] = useState<any>({});
  const [isModalLogin, setIsModalLogin] = useState<boolean>(false);
  const [isModalService, setIsModalService] = useState<boolean>(false);
  const [wxPayParams, setWxPayParams] = useState<any>({nonce_str: '', prepay_id: '', appid: ''})
  const navigate = useNavigate();
  const location = useLocation();
  const { message } = useApp();
  // const abortController = useRef<AbortController>(null);
  const scrollRef = useRef<ScrollDotHandle>(null);
  const senderRef = useRef<any>(null);
//   const getContent = (lines: any) => {
//     const _content = lines.map((line: string, idx: number) => {
//         line = line.replace('\n\n', '');
//         let markdownFlag = false;
//         if (line.indexOf('data:') >= 0) {
//           line = line.replace('data:', '');
//           // 在qwen-tur
//           if (/^(#{1,6}\s|[-*+]\s|>\s|```|[*]{3,}|[-]{3}|\d+\.\s)/.test(line)) {
//             line = '\n' + line;
//             markdownFlag = true;
//           }
//           if (/.*```$/.test(line)) {
//             line = line + '\n';
//           }
//           if (idx > 0 && line.indexOf('|') === 0 && (/^.*[:|：]$/.test(lines[idx - 1]) || /^(#{1,6}\s|[-*+]\s|>\s|```|[*]{3,}|[-]{3}|\d+\.\s)/.test(lines[idx - 1]))) {
//             line = '\n' + line;
//           } 
//         } else {
//           line = '\n' + line;
//         }
//         if (!line) {
//           line = '\n';
//         }
//         if (line === '\n---') {
//          line = '\n\n---\n\n'; 
//         }
//         console.log('rtline==', line);
//         return line;
//     });
//     return _content.join('');
// };
    const stateRef = useRef({
      sessionId: null as string | null,
      messageSending: 'stop',
      rtmsg: null,
      usermsg: null as string | null,
      aimsg: null as string | null
    });

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    const _sessions = loadChatSessions();
    setChatSessions(_sessions);
    if (paramSessionId) {
      if (_.findIndex(_sessions, { key: paramSessionId }) < 0) {
        addNewSession();
        navigate({pathname: '/apps/chat', search: location.search, hash: location.hash}, {state: location.state}); //无效的对话参数，被转到新对话
        
      } else {
        setSessionId(paramSessionId);
        setChatSessions(_sessions);
        setMessages(loadMessages(paramSessionId || ''));
      }
    } else {
      addNewSession();
    }
    setShouldScroll(true);
    getLoginInfo();
    towxrecharge();
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, []);
  
  useEffect(() => {
    stateRef.current = { 
      sessionId, 
      messageSending ,
      rtmsg,
      usermsg,
      aimsg
    };
  }, [sessionId, messageSending, rtmsg, usermsg, aimsg]);

  useEffect(() => {
    if (wxPayParams.nonce_str && wxPayParams.prepay_id && wxPayParams.appid) {
      wxpay();
    }
  }, [wxPayParams]);

  const handleBeforeUnload = () => {
    const { sessionId, messageSending, rtmsg, usermsg, aimsg } = stateRef.current;
    if (sessionId && messageSending !== 'stop' && rtmsg) {
      const _rtmsg = rtmsg ? rtmsg : {id: `${Date.now()}`, askmessage: usermsg, answermessage: aimsg, sessionId };
      const _usermsg: Message = { key: `${_rtmsg.id}_0`, content: _rtmsg.askmessage || '', placement: 'end' };
      const _replymsg: Message = { key: `${_rtmsg.id}_1`, content: (messageSending === 'interrupt' ? aimsg : _rtmsg.answermessage) || '', placement: 'start' };
      const _messages = loadMessages(sessionId);
      _messages.push(_usermsg);
      _messages.push(_replymsg);
      localStorage.setItem(`bublelist${_rtmsg.sessionId}`, JSON.stringify(_messages));
    }
  };

  const getLoginInfo = () => {
    axios.get('/api/system/self').then((res) => {
      const userInfo = res && res.data && res.data.id
        ? {
          userId: res.data.id,
          userName: res.data.name,
          schoolCode: res.data.schoolUser ? res.data.schoolUser.schoolCode : '',
          role: res.data.schoolUser ? res.data.schoolUser.role : '',
          phoneNum: res.data.phoneNum ? res.data.phoneNum : '',
        }
        : {}
        setUserInfo(userInfo);
        localStorage.setItem('userId', userInfo.userId);
    }).catch((error) => {
      console.log('error=', error);
      setUserInfo({});
      localStorage.setItem('userId', '');
    })
  }

  

  const menuConfig: ConversationsProps['menu'] = (conversation) => ({
    items: [
      {
        label: '编辑名称',
        key: '0',
      },
      {
        label: '删除对话',
        key: '1',
      },
    ],
    onClick: (menuInfo) => {
      menuInfo.domEvent.stopPropagation();
      if (menuInfo.key === '1') {
        removeSession(conversation.key);
      } else if (menuInfo.key === '0') {
        setIsModalOpen(true);
        const _s: any = _.find(chatSessions, { key: sessionId || '' });
        setInputSessionName(_s.label);
      }
    },
  });

  const menuProps = {
    items: [
      { label: <a>编辑名称</a>, key: '0' },
      { label: <a>删除对话</a>, key: '1' },
    ],
    onClick: (e: any) => {
      if (e.key === '1') {
        removeSession(sessionId || '');
      } else if (e.key === '0') {
        setIsModalOpen(true);
        const _s: any = _.find(chatSessions, { key: sessionId || '' });
        setInputSessionName(_s.label);
      }
    },
  };

  // 新建对话
  const addNewSession = () => {
    setSessionId('');
    setMessages([]);
    setRtmsg(null);
    navigate({pathname: '/apps/chat', search: location.search, hash: location.hash}, {state: location.state});
  };

  const changeSession = (sessionId: string) => {
    setSessionId(sessionId);
    localStorage.setItem('sessionId', sessionId);
    const messages = loadMessages(sessionId);
    setMessages(messages);
    navigate({pathname: `/apps/chat/${sessionId}`, search: location.search, hash: location.hash}, {state: location.state});
  }

  // const transformSSEToRawStream = (response: Response): Response => {
  //   const decoder = new TextDecoder();
  //   const encoder = new TextEncoder();
  //   let buffer = "";

  //   return new Response(
  //       new ReadableStream({
  //           async start(controller) {
  //               const reader = response.body?.getReader();
  //               if (!reader) {
  //                   controller.close();
  //                   return;
  //               }

  //               try {
  //                   console.log('messageSending=', messageSending);
  //                   console.log('abortController.current?.signal=', abortController.current?.signal);
  //                   while (true && messageSending !== 'interrupt') {
  //                       const { done, value } = await reader.read();
  //                       if (done) {
  //                           // 处理剩余缓冲区数据
  //                           if (buffer) {
  //                               controller.enqueue(encoder.encode(buffer));
  //                           }
  //                           break;
  //                       }
  //                       // 解码并追加到缓冲区
  //                       buffer += decoder.decode(value, { stream: true });
  //                       // 按SSE事件分隔符拆分
  //                       const events = buffer.split(/\n\n/);
  //                       buffer = events.pop() || ""; // 保留未完成事件
  //                       // 处理完整事件
  //                       for (const event of events) {
  //                           if (!event.trim()) continue;
  //                           // 直接转发原始SSE格式数据
  //                           controller.enqueue(encoder.encode(event + "\n\n"));
  //                       }
  //                   }
  //                   controller.close();
  //               } catch (err) {
  //                   controller.error(`SSE流处理失败: ${err}`);
  //               }
  //           }
  //       }),
  //       { 
  //           headers: { 
  //               "Content-Type": "text/event-stream",
  //               "Cache-Control": "no-cache",
  //               "Connection": "keep-alive"
  //           } 
  //       }
  //   );
  // };

  // const apiRequest = XRequest({
  //   baseURL: `/api/aliaiagent/chat/${sessionId}`,
  //   model: 'qwen-turbo',
  //   fetch: async (url, options: any) => {
  //     const { body } = JSON.parse(options.body);
  //     try {
  //       const response = await fetch(url, {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json", ...options.headers },
  //         body: JSON.stringify(body),
  //       });
  //       if (!response.ok) {
  //         throw new Error(`HTTP ${response.status}`);
  //       }
  //       // 3. 转换 SSE 流为 NDJSON
  //       if (response.headers.get("content-type")?.includes("text/event-stream")) {
  //         return transformSSEToRawStream(response);
  //       }
  //       return response;
  //     } catch (err: any) {
  //       return new Response(JSON.stringify({ error: err.message }), { status: 500 });
  //     }
  //   },
  // });

  // const submitAndGcStream = async (v: any) => {
  //   const data: any = { askmessage: v, sessionId };
  //   delete data.loading;
  //   const neednaviage = !(sessionId);

  //   await apiRequest.create(
  //     {
  //       body: data,
  //       stream: true,
  //     },
  //     {
  //       onSuccess: (mgs) => {
  //         if (!mgs || mgs.length < 1) {
  //           setMessageSending('stop');
  //           return;
  //         }
  //         const _f = mgs[0].replace('\n\n','').replace('data:','');
  //         const _l = mgs[mgs.length - 1].replace('\n\n','').replace('data:','');
  //         let _sessionId = sessionId;
  //         if (neednaviage && _f.indexOf('newchatsession|') >= 0) {
  //           mgs.shift(); // 移除第一个
  //           const _chatSessions = _.cloneDeep(chatSessions);
  //           const tmparr = _f.split('|');  
  //           const _chatSession = { key: tmparr[1], label: tmparr[2] };
  //           _chatSessions.push(_chatSession);
  //           pushChatSessions(_chatSession);
  //           setSessionId(tmparr[1]);
  //           localStorage.setItem('sessionId', tmparr[1]);
  //           setChatSessions(_chatSessions);
  //           _sessionId = tmparr[1];
  //         }
  //         mgs.pop();
  //         const tmparr = _l.split('|');
  //         const askmsg: Message = { key: `${tmparr[1]}_0`, content: v, placement: 'end', loading: false }; 
  //         const replymsg: Message = { key: `${tmparr[1]}_1`, content: getContent(mgs), placement: 'start', loading: false };
  //         const _messages: any = _.cloneDeep(messages);
  //         _messages.push(askmsg);
  //         _messages.push(replymsg);
  //         localStorage.setItem(`bublelist${_sessionId}`, JSON.stringify(_messages));
  //         setMessages(_messages);
  //         setMessageSending('stop');
  //         if (neednaviage) {
  //           navigate(`/apps/chat/${_sessionId}`);
  //         }
  //       },
  //       onError: (error) => {
  //         console.error('onError', error);
  //       },
  //       onUpdate: (msg) => {
  //         if (msg.indexOf('newchatsession|') < 0 && msg.indexOf('messagelogid|') < 0) {
  //           setLines((pre: any) => [...pre, msg]);
  //         }
  //       },
  //       onStream: (controller) => {
  //         abortController.current = controller;
  //       },
  //     },
  //     new TransformStream<string, string>({
  //       transform(chunk, controller) {
  //         controller.enqueue(chunk);
  //       },
  //     }),
  //   );
  // }

  const completeTyping = () => {
    if (messageSending === 'interrupt' || !rtmsg || _.findIndex(messages, {key: `${rtmsg.id}_1`}) >= 0) {
      return;
    }
    saveMsg();
  }

  const saveMsg = () => {
    if (!sessionId) {
      // 如果是新会话，还没有sessionId，不会保存会议记录
      return;
    }
    const _rtmsg = rtmsg ? rtmsg : {id: `${Date.now()}`, askmessage: usermsg, answermessage: aimsg, sessionId };
    const _usermsg: Message = { key: `${_rtmsg.id}_0`, content: _rtmsg.askmessage, placement: 'end' };
    const _replymsg: Message = { key: `${_rtmsg.id}_1`, content: messageSending === 'interrupt' ? aimsg : _rtmsg.answermessage, placement: 'start' };
    const _messages = _.cloneDeep(messages);
    _messages.push(_usermsg);
    _messages.push(_replymsg);
    setMessages(_messages);
    localStorage.setItem(`bublelist${_rtmsg.sessionId}`, JSON.stringify(_messages));
    setMessageSending('stop');
    if (!sessionId) {
      navigate({pathname: `/apps/chat/${_rtmsg.sessionId}`, search: location.search, hash: location.hash}, {state: location.state});
    }
  }

  const showError = (error: any) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 403) {
        setIsModalLogin(true);
        setMessageSending('stop');
        setUserInfo({});
      } else {
        message.error(data.desc || `服务器错误: ${status}`);
      }
    } else {
      message.error("网络异常，请检查连接");
    }
  }

  const handleSubmit = () => {
    if (!userInfo.userId) {
      setIsModalLogin(true);
      return;
    }
    if (!inputValue.trim()) return;
    if (messageSending === 'interrupt') {
      const articles = document.getElementsByClassName('ant-bubble-start');
      const last: any = articles[articles.length - 1];
      if (last.style.filter === 'blur(10px)') {
        last.style.filter = 'blur(0)';
      } else {
        // 中止，但需要保留
        saveMsg();
      }
    }
    setMessageSending('thinking');
    setRtmsg(null);
    const _v = inputValue;
    setUsermsg(_v);
    setAimsg('思考中...');
    setDuration(-1);
    // setLines([]);
    setInputValue('');
    // submitAndGcStream(_v);
    axios.post(`/api/aliaiagent/chat/${sessionId}`, { askmessage: _v }).then((res) => {
      if (res.status === 200 && res.data.success) {
        if (messages.length < 1 && res.data.data.chatsession) {
          const _chatSessions = _.cloneDeep(chatSessions);
          const _chatSession = { key: res.data.data.chatsession.id, label: res.data.data.chatsession.name };
          _chatSessions.push(_chatSession);
          pushChatSessions(_chatSession);
          setSessionId(res.data.data.chatsession.id);
          localStorage.setItem('sessionId', res.data.data.chatsession.id);
          setChatSessions(_chatSessions);
        }
        const msg2in1 = res.data.data.messagelog;
        setRtmsg(msg2in1);
        setAimsg(msg2in1.answermessage);
        setMessageSending('sending');
        setDuration(Date.now());
      } else {
        setMessageSending('stop');
        if (res.data.count === -1) {
          setIsModalService(true);
        } else {
          message.error(res.data.desc || res.statusText)
        }
      }
    }).catch((error) => {
      showError(error);
      setMessageSending('stop');
    });
  };

  // 非流的模式处理消息发送
  // const handleSubmit = () => {
  //   if (!inputValue.trim()) return;
    
  //   const newMessage: Message = {
  //     key: `${messages.length + 1}`,
  //     content: inputValue,
  //     placement: 'end',
  //     loading: false,
  //   };
  //   const aiThinking: Message = {
  //     key: 'ai',
  //     placement: 'start',
  //     content: '正在思考中...',
  //     loading: true,
  //     loadingRender: () => '正在思考中...',
  //   };
    
  //   const updatedMessages = [...messages, newMessage, aiThinking];
  //   setInputValue('');
  //   setMessageSending(true);
  //   setMessages(updatedMessages);
  //   const data: any = { askmessage: newMessage.content, sessionId };
  //   delete data.loading;
  //   axios.post(`/api/aliaiagent/chat/${sessionId}`, data).then((res) => {
  //     if (res.data.success) {
  //       let neednaviage = false;
  //       if (messages.length < 1 && res.data.data.chatsession) {
  //         const _chatSessions = _.cloneDeep(chatSessions);
  //         const _chatSession = { key: res.data.data.chatsession.id, label: res.data.data.chatsession.name };
  //         _chatSessions.push(_chatSession);
  //         pushChatSessions(_chatSession);
  //         setSessionId(res.data.data.chatsession.id);
  //         localStorage.setItem('sessionId', res.data.data.chatsession.id);
  //         setChatSessions(_chatSessions);
  //         neednaviage = true;
  //       }
  //       const msg2in1 = res.data.data.messagelog;
  //       const replymsg: Message = { key: `${msg2in1.id}_1`, content: msg2in1.answermessage, placement: 'start', loading: false };
  //       updatedMessages[updatedMessages.length - 2].key = `${msg2in1.id}_0`;
  //       updatedMessages[updatedMessages.length - 1] = replymsg;
  //       const _updatedMessages = _.cloneDeep(updatedMessages);
  //       setMessages(_updatedMessages);
  //       localStorage.setItem(`bublelist${msg2in1.sessionId}`, JSON.stringify(_updatedMessages));
  //       setMessageSending(false);
  //       if (neednaviage) {
  //         navigate(`/apps/chat/${msg2in1.sessionId}`);
  //       }
  //     }
  //   })
  // };

  // 处理取消发送
  const handleCancel = () => {
    // abortXRequest();
    if (duration === -1) {
      // const _rtmsg = _.cloneDeep(rtmsg);
      // _rtmsg.answermessage = '已暂停生成。';
      setMessageSending('interrupt');
      setAimsg('已暂停生成。')
    } else {
      const ltime = Date.now() - duration;
      const chars = ltime / 100;
      if (chars < rtmsg.answermessage.length) {
        // const _rtmsg = _.cloneDeep(rtmsg);
        // _rtmsg.answermessage = rtmsg.answermessage.substring(0, chars + 1);
        setMessageSending('interrupt');
        setAimsg(rtmsg.answermessage.substring(0, chars + 1));
      }
    }
  };

  const handleUpdateName = () => {
    if (!userInfo.userId) {
      setIsModalLogin(true);
      return;
    }
    if (inputSessionName && inputSessionName.trim()) {
      if (inputSessionName.length > 60) {
        message.error('对话名称不要超为过60个字符!');
        return;
      }
      const __v = inputSessionName.trim();
      axios.post(`/api/aliaiagent/chat/updatename/${sessionId}`, { name: __v }).then((res) => {
        if (res.data.success) {
          const _chatSessions = _.cloneDeep(chatSessions);
          const idx = _.findIndex(_chatSessions, { key: sessionId || ''});
          _chatSessions[idx].label = __v;
          setChatSessions(_chatSessions);
          localStorage.setItem(`chatSessions${userInfo.userId}`, JSON.stringify(_chatSessions));
          setIsModalOpen(false);
        }
      }).catch((error) => {
        showError(error);
      });
    } else {
      message.error('请输入正确的对话名称!');
    }
  }

  const removeSession = (sid: string) => {
    if (!userInfo.userId) {
      setIsModalLogin(true);
      return;
    }
    const _chatSessions = _.cloneDeep(chatSessions);
    _.remove<ChatSession[]>(_chatSessions, item => item.key === sid);
    setChatSessions(_chatSessions);
    localStorage.setItem(`chatSessions${userInfo.userId}`, JSON.stringify(_chatSessions));
    if (sessionId === sid) {
      setMessages([]);
      setSessionId('');
      axios.delete(`/api/aliaiagent/chat/${sid}`).then((res) => {
        if (res.data.success) {
          addNewSession();
        }
      }).catch((error) => {
        showError(error);
      });
    }
  }

  const reAsk = () => {
    const articles = document.getElementsByClassName('ant-bubble-start');
    const last: any = articles[articles.length - 1];
    last.style.filter = 'blur(10px)';
    setInputValue(usermsg);
    senderRef.current.focus();
  }

  const toLogin = () => {
    window.location.href = `/apps/login?fromUrl=${encodeURIComponent(document.location.pathname)}`
  }

  const toLogout = () => {
    axios.get('/app/system/logout');
    setUserInfo({});
  }

  const checkWeiXinNav = () => {
    const wechatInfo = window.navigator.userAgent.match(/MicroMessenger\/([\d\\.]+)/i);
    if (wechatInfo && wechatInfo[1] >= '5.0') {
      return true;
    }
    return false;
  }

  const towxrecharge = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code');
    const attach = queryParams.get('state');
    if (checkWeiXinNav() && code && attach) {
      // 进微信支付
      wxrecharge();
    }
  }

  const wxrecharge = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code');
    const attach = decodeURIComponent(queryParams.get('state') || '');
    const paramsObject = JSON.parse(attach);
    const { userId, price, tokens } = paramsObject;
      // const bizUrlNext = window.location.pathname.substring(1);
      // const paramsObject = this.paramsToObject(attach);
    axios.get(`/api/weixinpay/openid/${code}`).then((res) => {
      if (res.data.success) {
        const opData = res.data.data;
        axios
          .post('/api/weixinpay/rechargealitokens', {
            tokens,
            userId,
            openid: opData.openid,
            weixinName: opData.nickname,
            price,
            attach,
          })
          .then((resp) => {
            const {
              data, success, count,
            } = resp.data;
            if (success) {
              if (count === 1) {
                setWxPayParams(
                  {
                    prepay_id: data.prepay_id,
                    nonce_str: data.nonce_str,
                    appid: data.appid,
                  });
              }
            } else {
              message.error('操作失败，稍后再试!');
            }
          });
      }
    });
  }

  const wxpay = () => {
    const WeixinJSBridge = window.WeixinJSBridge;
    if (typeof WeixinJSBridge === 'undefined') {
      if (window.document.addEventListener) {
        window.document.addEventListener('WeixinJSBridgeReady', onBridgeReady, false);
      }
      //  else if (window.document.attachEvent) {
      //   window.document.attachEvent('WeixinJSBridgeReady', onBridgeReady);
      //   window.document.attachEvent('onWeixinJSBridgeReady', onBridgeReady);
      // }
    } else {
      onBridgeReady();
    }
  }

  const randomString = (len: number) => {
    const leng = len || 32;
    const _chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678'; /** **默认去掉了容易混淆的字符oOLl,9gq,Vv,Uu,I1*** */
    const maxPos = _chars.length;
    let pwd = '';
    for (let i = 0; i < leng; i += 1) {
      pwd += _chars.charAt(Math.floor(Math.random() * maxPos));
    }
    return pwd;
  }

  const generateSign = (appId: string, nonceStr: string, signType: string, pack: string, timeStamp: number) => {
      let Str = `appId=${appId}&nonceStr=${nonceStr}&package=${pack}&signType=${signType}&timeStamp=${timeStamp}`;
      Str += '&key=itbeginwaen2018db56753388jgfv211';
      const sign = md5(Str).toUpperCase();
      return sign;
    }

  const onBridgeReady = () => {
    const { appid, prepay_id } = wxPayParams;
    // const queryParams = new URLSearchParams(window.location.search);
    // const attach = decodeURIComponent(queryParams.get('state') || '');
    // const paramsObject = paramsToObject(attach);
    // const { userId } = paramsObject;
    const bizUrlNext = location.pathname;
    const timeStamp10: number = Number(new Date().getTime().toString().substring(0, 10));
    const packageWx = `prepay_id=${prepay_id}`;
    const nonce_str = randomString(31);
    const sign = generateSign(appid, nonce_str, 'MD5', packageWx, timeStamp10);
    window.WeixinJSBridge?.invoke(
      'getBrandWCPayRequest',
      {
        appId: appid, // 公众号名称，由商户传入
        timeStamp: `${timeStamp10}`, // 时间戳，自1970年以来的秒数
        nonceStr: nonce_str, // 随机串
        package: packageWx,
        signType: 'MD5', // 微信签名方式：
        paySign: sign, // 微信签名
      },
      (res: any) => {
        if (res.err_msg === 'get_brand_wcpay_request:ok') {
          window.location.href = bizUrlNext;
        } // 使用以上方式判断前端返回,微信团队郑重提示：res.err_msg将在用户支付成功后返回    ok，但并不保证它绝对可靠。
      },
    );
  }

  const toBuy = (r:any) => {
    if (checkWeiXinNav()) {
       // 在微信里，默认微信支付
      const _data = {
        userId: userInfo.userId,
        tokens: r.tokens,
        price: r.price
      };
      const state = encodeURIComponent(JSON.stringify(_data));
      let url = window.location.href;
      const encodeUri = encodeURIComponent(url);
      window.location.href = 'https://open.weixin.qq.com/connect/oauth2/authorize?appid='
      + `wx973c5b220058cbed&redirect_uri=${encodeUri}`
      + `&response_type=code&scope=snsapi_userinfo&state=${state}`
      + '#wechat_redirect';
    } else {
      // 其他情况默认到支付宝，支付宝又分手机客户端还是pc网页上支付
      try {
        const bizUrlNext = window.location.pathname.substring(1);
        axios.post(`/api/pay/${isMobile().any ? 'wap' : 'page'}/buyalitokens/${userInfo.userId}/${r.tokens}`, null, { params: { price: r.price, nextBizUrl: bizUrlNext } }).then((res1) => {
          if (res1.data.success && res1.data.count === 1) {
            document.write(res1.data.data);
          }
        })
      } catch (error) {
        message.error('操作失败，稍后再试!');
      }
    }
  }

  return (
    <XProvider direction="ltr">
      <Flex style={{ height: '100vh', width: '100vw', boxSizing: 'border-box', padding: '5px' }} gap={12}>
        <Sidebar 
          showLeftBar={showLeftBar}
          setShowLeftBar={setShowLeftBar}
          chatSessions={chatSessions}
          addNewSession={addNewSession}
          sessionId={sessionId}
          changeSession={changeSession}
          menuConfig={menuConfig}
          userInfo={userInfo}
          toLogin={toLogin}
          toLogout={toLogout}
        />
        
        <Flex vertical style={{ flex: 1, position: 'relative' }} gap={8}>
          <ChatHeader 
            showLeftBar={showLeftBar}
            setShowLeftBar={setShowLeftBar}
            addNewSession={addNewSession}
            chatSessions={chatSessions}
            sessionId={sessionId}
            menuProps={menuProps}
          />
          
          <MessageList
            messages={messages}
            usermsg={usermsg}
            aimsg={aimsg}
            messageSending={messageSending}
            shouldScroll={shouldScroll}
            completeTyping={completeTyping}
            reAsk={reAsk}
            ref={scrollRef}
          />
          {/* <PromptSuggestions /> */}
          <div style={{
            position: 'sticky',
            bottom: 0,
            background: 'white',
            padding: '16px 10px',
            zIndex: 100,
          }}
            id="sendmessagediv"
          >
            <Sender 
              value={inputValue}
              loading={messageSending === 'thinking' || messageSending === 'sending'}
              onChange={(v) => setInputValue(v)}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              ref={senderRef}
            />
          </div>
        </Flex>
        
        {/* 思维链组件（可选） */}
        {false && (
          <>
            <Divider type="vertical" style={{ height: '100%' }} />
            <ThoughtChain
              style={{ width: 200 }}
              items={[
                { title: '思考中...', status: 'pending', icon: <LoadingOutlined /> }
              ]}
            />
          </>
        )}
      </Flex>
      <Modal
        title="编辑对话名称"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isModalOpen}
        onOk={handleUpdateName}
        onCancel={() => setIsModalOpen(false) }
        okText="确定"
        cancelText="取消"
      >
        <Input value={inputSessionName} onChange={(e) => setInputSessionName(e.target.value) } />
      </Modal>
      <Modal
        title="登录提示"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isModalLogin}
        onOk={toLogin}
        onCancel={() => setIsModalLogin(false) }
        okText="确定"
        cancelText="取消"
      >
        <p>先登录，马上回来?</p>
      </Modal>
      <Modal
        title="服务购买"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isModalService}
        onCancel={() => setIsModalService(false) }
        footer={null}
      >
        <p>您当前的tokens为0，会影响你的使用，请及时购买tokens服务</p>
        <Table
          pagination={false}
          dataSource={[
            {
              tokens: '10000',
              period: '一年',
              price: '60',
            },
            {
              tokens: '60000',
              period: '一年',
              price: '350',
            },
            {
              tokens: '600000',
              period: '一年',
              price: '3000',
            },
          ]}
          columns={[
            {
              title: 'tokens(单位千tokens)',
              dataIndex: 'tokens',
              key: 'tokens',
            },
            {
              title: '有效期',
              dataIndex: 'period',
              key: 'period',
            },
            {
              title: '价格',
              dataIndex: 'price',
              key: 'price',
              render: (text) => <><span>¥</span>{text}</>
            },
            {
              title: '',
              dataIndex: 'action',
              key: 'action',
              render: (text, record) => <Button onClick={() => { toBuy(record) }}>购买</Button>,
            },
          ]} 
        />
      </Modal>
    </XProvider>
  );
};

export default AiApp;